import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Loader2, X, CheckCircle2, AlertCircle, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

interface SyncState {
  status: "idle" | "syncing" | "paused" | "paused_limit" | "completed" | "error";
  total_emails: number;
  processed_emails: number;
  imported_documents: number;
  skipped_documents: number;
  error_message: string | null;
}

interface GmailSyncProgressProps {
  syncState: SyncState | null;
  syncing: boolean;
  onCancel: () => void;
  onReset: () => void;
  className?: string;
}

export function GmailSyncProgress({ 
  syncState, 
  syncing, 
  onCancel, 
  onReset,
  className 
}: GmailSyncProgressProps) {
  if (!syncState || syncState.status === "idle") {
    return null;
  }

  // Gmail's resultSizeEstimate is unreliable, so cap progress at 99% until complete
  // and use a more realistic calculation
  const rawProgress = syncState.total_emails > 0 
    ? Math.round((syncState.processed_emails / syncState.total_emails) * 100) 
    : 0;
  const progress = Math.min(rawProgress, 99); // Cap at 99% until actually complete

  const isComplete = syncState.status === "completed";
  const isError = syncState.status === "error";
  const isPaused = syncState.status === "paused";
  const isPausedLimit = syncState.status === "paused_limit";
  const isSyncing = syncState.status === "syncing" || syncing;

  return (
    <div className={cn(
      "rounded-lg border p-3 space-y-2",
      isComplete && "border-green-500/30 bg-green-500/5",
      isError && "border-destructive/30 bg-destructive/5",
      isPaused && "border-muted-foreground/30 bg-muted/10",
      isPausedLimit && "border-amber-500/30 bg-amber-500/5",
      isSyncing && "border-primary/30 bg-primary/5",
      className
    )}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isSyncing && (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm font-medium">Synchronisation en cours...</span>
            </>
          )}
          {isPaused && (
            <>
              <AlertCircle className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Synchronisation en pause</span>
            </>
          )}
          {isPausedLimit && (
            <>
              <TrendingUp className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-600">Limite de documents atteinte</span>
            </>
          )}
          {isComplete && (
            <>
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-green-600">Synchronisation terminée</span>
            </>
          )}
          {isError && (
            <>
              <AlertCircle className="w-4 h-4 text-destructive" />
              <span className="text-sm font-medium text-destructive">Erreur de synchronisation</span>
            </>
          )}
        </div>

        {isSyncing && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-muted-foreground hover:text-foreground"
            onClick={onCancel}
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Mettre en pause
          </Button>
        )}

        {(isPaused || isComplete || isError) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-muted-foreground hover:text-foreground"
            onClick={onReset}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Progress bar */}
      {isSyncing && (
        <div className="space-y-1">
          <Progress value={progress} className="h-2" />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {syncState.processed_emails} emails traités
            </span>
            <span>{progress}%</span>
          </div>
        </div>
      )}

      {/* Storage limit upgrade CTA */}
      {isPausedLimit && (
        <div className="space-y-2">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {syncState.error_message || "Vous avez atteint la limite de documents de votre plan."}
          </p>
          <div className="flex gap-2">
            <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
              <Link to="/pricing">
                <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
                Passer à un plan supérieur
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
            >
              Fermer
            </Button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center gap-4 text-xs">
        <span className="text-green-600 font-medium">
          ✓ {syncState.imported_documents} importé{syncState.imported_documents > 1 ? "s" : ""}
        </span>
        {syncState.skipped_documents > 0 && (
          <span className="text-muted-foreground">
            {syncState.skipped_documents} déjà présent{syncState.skipped_documents > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Error message */}
      {isError && syncState.error_message && (
        <p className="text-xs text-destructive">{syncState.error_message}</p>
      )}
    </div>
  );
}
